/**
 * Delivery Estimates Component
 *
 * Listens for variant changes and updates delivery estimates dynamically
 * when a variant with different delivery data is selected.
 *
 * Also listens for SKU picker price changes and updates the shipping fee label
 * based on the shipping threshold configured for the market.
 *
 * Supports Christmas Last Order Dates (LOD) with countdown functionality.
 * When LOD data is present, replaces normal delivery estimates with LOD messaging.
 *
 * All date calculations are done server-side in the Liquid template.
 */

import { ThemeEvents } from '@theme/events';

class DeliveryEstimates extends HTMLElement {
  connectedCallback() {
    // Get shipping threshold and fee from data attributes
    this.shippingThreshold = parseFloat(this.getAttribute('data-shipping-threshold')) || 0;
    this.shippingFee = parseFloat(this.getAttribute('data-shipping-fee')) || 0;
    this.shippingFeeFormatted = this.getAttribute('data-shipping-fee-formatted') || '';
    this.shippingThresholdFormatted = this.getAttribute('data-shipping-threshold-formatted') || '';

    // Get Christmas LOD data
    this.lodStandard = this.getAttribute('data-lod-standard');
    this.lodExpress = this.getAttribute('data-lod-express');

    // Get translation parts
    this.translations = {
      orderBy: this.getAttribute('data-t-order-by') || 'Order by',
      forDelivery: this.getAttribute('data-t-for-delivery') || 'for Christmas delivery',
      expressNote: this.getAttribute('data-t-express-note') || 'Select Express Shipping at checkout.',
      christmasMissed: this.getAttribute('data-t-christmas-missed') || 'Order now – may not arrive by Christmas',
      countdownPrefix: this.getAttribute('data-t-countdown-prefix') || 'Order within',
      countdownSuffix: this.getAttribute('data-t-countdown-suffix') || 'for Christmas delivery',
      freeShippingItem: this.getAttribute('data-t-free-shipping-item') || '<strong>FREE</strong> shipping on this item',
      freeShippingOver: this.getAttribute('data-t-free-shipping-over') || '<strong>FREE</strong> shipping on orders over __THRESHOLD__'
    };

    // Listen for variant update events
    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate.bind(this));

    // Listen for SKU picker selection changes (price updates)
    document.addEventListener('sku-picker:selection-changed', this.handlePriceUpdate.bind(this));

    // Initialize LOD or fallback to print run countdown
    this.initializeLOD();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate.bind(this));
    document.removeEventListener('sku-picker:selection-changed', this.handlePriceUpdate.bind(this));
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  /**
   * Initialize LOD display or fall back to print run countdown
   */
  initializeLOD() {
    const lodState = this.getLODState();

    if (lodState.hasLOD) {
      // Show LOD messaging, hide normal delivery row and print run countdown
      this.showLODState(lodState);
      this.startLODCountdown(lodState);
    } else if (lodState.type === 'missed') {
      // Show warning message but also show normal delivery and print run countdown
      this.showMissedWarning();
      this.startCountdownTimer();
    } else {
      // No LOD data - show normal delivery estimates and print run countdown
      this.startCountdownTimer();
    }
  }

  /**
   * Determine the current LOD state
   * @returns {Object} LOD state with type and relevant dates
   */
  getLODState() {
    const now = new Date();
    const standardDate = this.lodStandard ? new Date(this.lodStandard) : null;
    const expressDate = this.lodExpress ? new Date(this.lodExpress) : null;

    // No LOD data at all
    if (!standardDate && !expressDate) {
      return { hasLOD: false, type: 'none' };
    }

    // Check if standard shipping cutoff is still available
    if (standardDate && now < standardDate) {
      return {
        hasLOD: true,
        type: 'standard',
        targetDate: standardDate,
        timeRemaining: standardDate - now
      };
    }

    // Standard passed - check if express is still available
    if (expressDate && now < expressDate) {
      return {
        hasLOD: true,
        type: 'express',
        targetDate: expressDate,
        timeRemaining: expressDate - now
      };
    }

    // All cutoffs passed - show warning but also normal delivery
    if (standardDate || expressDate) {
      return { hasLOD: false, type: 'missed' };
    }

    return { hasLOD: false, type: 'none' };
  }

  /**
   * Show the appropriate LOD state in the UI
   * @param {Object} lodState - The LOD state object
   */
  showLODState(lodState) {
    const lodRow = this.querySelector('.delivery-estimates__lod-row');
    const deliveryRow = this.querySelector('.delivery-estimates__delivery-row');
    const urgencyRow = this.querySelector('.delivery-estimates__urgency');
    const lodIcon = this.querySelector('.delivery-estimates__lod-icon');
    const lodMessage = this.querySelector('.delivery-estimates__lod-message');

    if (!lodRow || !lodMessage) return;

    // Hide normal delivery row and print run countdown when LOD is active
    if (deliveryRow) deliveryRow.style.display = 'none';
    if (urgencyRow) urgencyRow.style.display = 'none';

    // Show LOD row
    lodRow.style.display = '';

    // Remove previous state classes
    lodRow.classList.remove('delivery-estimates__lod-row--standard', 'delivery-estimates__lod-row--express', 'delivery-estimates__lod-row--missed');

    // Set message and styling based on state
    if (lodState.type === 'standard') {
      lodRow.classList.add('delivery-estimates__lod-row--standard');
      lodIcon.textContent = '✓';
      this.updateLODMessage(lodState, false);
    } else if (lodState.type === 'express') {
      lodRow.classList.add('delivery-estimates__lod-row--express');
      lodIcon.textContent = '✓';
      this.updateLODMessage(lodState, false);
    } else if (lodState.type === 'missed') {
      lodRow.classList.add('delivery-estimates__lod-row--missed');
      lodIcon.textContent = '⚠';
      lodMessage.innerHTML = this.translations.christmasMissed;
    }
  }

  /**
   * Show missed LOD warning appended to delivery message
   */
  showMissedWarning() {
    const lodRow = this.querySelector('.delivery-estimates__lod-row');
    const deliveryRow = this.querySelector('.delivery-estimates__delivery-row');
    const urgencyRow = this.querySelector('.delivery-estimates__urgency');
    const deliveryMessage = this.querySelector('.delivery-estimates__delivery-message');

    // Hide LOD row, show normal delivery rows
    if (lodRow) lodRow.style.display = 'none';
    if (deliveryRow) deliveryRow.style.display = '';
    if (urgencyRow) urgencyRow.style.display = '';

    // Append warning to delivery message if not already there
    // Check for gift card link since the message is now HTML
    if (deliveryMessage && !deliveryMessage.innerHTML.includes('gift-card')) {
      deliveryMessage.innerHTML = deliveryMessage.innerHTML + '. ' + this.translations.christmasMissed;
    }
  }

  /**
   * Update the LOD message with date or countdown
   * @param {Object} lodState - The LOD state object
   * @param {boolean} useCountdown - Whether to show countdown format
   */
  updateLODMessage(lodState, useCountdown = false) {
    const lodMessage = this.querySelector('.delivery-estimates__lod-message');
    if (!lodMessage || lodState.type === 'missed') return;

    const isExpress = lodState.type === 'express';
    const t = this.translations;

    // Check if we should show countdown (< 48 hours remaining)
    const hoursRemaining = lodState.timeRemaining / (1000 * 60 * 60);
    const showCountdown = useCountdown || hoursRemaining < 48;

    let message;
    if (showCountdown && lodState.timeRemaining > 0) {
      // Show countdown format
      const hours = Math.floor(lodState.timeRemaining / (1000 * 60 * 60));
      const minutes = Math.floor((lodState.timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
      const timeStr = hours > 0 ? `<strong>${hours}h ${String(minutes).padStart(2, '0')}m</strong>` : `<strong>${minutes}m</strong>`;

      message = `${t.countdownPrefix} ${timeStr} ${t.countdownSuffix}`;
      if (isExpress) {
        message += `<br>${t.expressNote}`;
      }
    } else {
      // Show date format
      const dateStr = this.formatLODDate(lodState.targetDate);
      message = `${t.orderBy} <strong>${dateStr}</strong> ${t.forDelivery}`;
      if (isExpress) {
        message += `<br>${t.expressNote}`;
      }
    }
    lodMessage.innerHTML = message;
  }

  /**
   * Format a date for LOD display (e.g., "Wed 18 Dec")
   * @param {Date} date - The date to format
   * @returns {string} Formatted date string
   */
  formatLODDate(date) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  }

  /**
   * Start the LOD countdown timer
   * @param {Object} initialState - Initial LOD state
   */
  startLODCountdown(initialState) {
    // Clear any existing interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Don't start countdown if already missed
    if (initialState.type === 'missed') return;

    const updateLOD = () => {
      const lodState = this.getLODState();

      if (lodState.type === 'missed') {
        // LOD expired - show warning but also show normal delivery
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        this.showMissedWarning();
        this.startCountdownTimer();
        return;
      }

      if (!lodState.hasLOD) {
        // No LOD at all - just show normal delivery
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        this.startCountdownTimer();
        return;
      }

      // Update message with current countdown
      this.updateLODMessage(lodState, true);
    };

    // Update every minute
    this.countdownInterval = setInterval(updateLOD, 60000);
  }

  /**
   * Handles variant update events from the native variant picker
   * @param {CustomEvent} event - The variant update event
   */
  handleVariantUpdate(event) {
    const { html } = event.detail.data;

    if (!html) return;

    // Find the updated delivery estimates in the new HTML
    const newDeliveryEstimates = html.querySelector('delivery-estimates');

    if (!newDeliveryEstimates) {
      // If no delivery estimates in the new HTML, hide this element
      this.style.display = 'none';
      return;
    }

    // Check if delivery estimates should be shown (has content)
    const hasContent = newDeliveryEstimates.querySelector('.delivery-estimates');

    if (hasContent) {
      // Update data attributes with new values
      this.shippingThreshold = parseFloat(newDeliveryEstimates.getAttribute('data-shipping-threshold')) || 0;
      this.shippingFee = parseFloat(newDeliveryEstimates.getAttribute('data-shipping-fee')) || 0;
      this.shippingFeeFormatted = newDeliveryEstimates.getAttribute('data-shipping-fee-formatted') || '';

      // Clear existing countdown interval before replacing content
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }

      // Update LOD data attributes from new estimates
      this.lodStandard = newDeliveryEstimates.getAttribute('data-lod-standard');
      this.lodExpress = newDeliveryEstimates.getAttribute('data-lod-express');

      // Replace the inner HTML with the new estimates (dates already calculated server-side)
      this.innerHTML = newDeliveryEstimates.innerHTML;
      this.style.display = '';

      // Re-initialize LOD (or fallback to print run countdown)
      this.initializeLOD();
    } else {
      // Hide if new variant doesn't have delivery estimates
      this.style.display = 'none';
    }
  }

  /**
   * Handles price updates from the SKU picker
   * Looks at the final product price element to determine current price
   */
  handlePriceUpdate() {
    // Get the current (paid) price from the price-current element specifically
    // Not the compare-at price which is in price-compare-at
    const priceElement = document.querySelector('.final-product-price .price-current');
    if (!priceElement) return;

    // Extract numeric price from the formatted text
    const priceText = priceElement.textContent.trim();
    const priceValue = this.parsePrice(priceText);

    // Update shipping label based on threshold
    this.updateShippingLabel(priceValue);
  }

  /**
   * Parse price from formatted string (e.g., "$99.99" -> 9999 cents)
   * @param {string} priceText - Formatted price string
   * @returns {number} Price in cents
   */
  parsePrice(priceText) {
    // Remove currency symbols and extract number
    const numericString = priceText.replace(/[^0-9.,]/g, '');
    const price = parseFloat(numericString.replace(',', ''));
    return isNaN(price) ? 0 : Math.round(price * 100);
  }

  /**
   * Update the shipping label based on current price
   * @param {number} currentPrice - Current price in cents
   */
  updateShippingLabel(currentPrice) {
    // Update badges mode label
    const labelElement = this.querySelector('.shipping-fee-label');
    if (labelElement && this.shippingThreshold && this.shippingFee) {
      if (currentPrice >= this.shippingThreshold) {
        labelElement.textContent = 'FREE';
      } else {
        labelElement.textContent = this.shippingFeeFormatted;
      }
    }

    // Update threshold mode message
    const messageElement = this.querySelector('.delivery-estimates__shipping-message');
    if (messageElement && this.shippingThreshold) {
      if (currentPrice >= this.shippingThreshold) {
        messageElement.innerHTML = this.translations.freeShippingItem;
      } else {
        messageElement.innerHTML = this.translations.freeShippingOver.replace('__THRESHOLD__', this.shippingThresholdFormatted);
      }
    }
  }

  /**
   * Start the countdown timer for print run cutoff
   * Cutoff is 2PM (14:00) local time each weekday
   */
  startCountdownTimer() {
    const countdownEl = this.querySelector('.delivery-estimates__countdown');
    const urgencyEl = this.querySelector('.delivery-estimates__urgency');
    if (!countdownEl || !urgencyEl) return;

    const updateCountdown = () => {
      const now = new Date();
      const cutoff1 = 13; // 1PM cutoff
      const cutoff2 = 22; // 10PM cutoff

      // Determine which cutoff to use
      let cutoff = new Date(now);
      const currentHour = now.getHours();

      if (currentHour < cutoff1) {
        // Before 1PM - countdown to 1PM
        cutoff.setHours(cutoff1, 0, 0, 0);
      } else if (currentHour < cutoff2) {
        // Between 1PM and 10PM - countdown to 10PM
        cutoff.setHours(cutoff2, 0, 0, 0);
      } else {
        // After 10PM - hide until tomorrow
        urgencyEl.style.display = 'none';
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        return;
      }

      // If somehow past the cutoff (edge case), hide
      if (now >= cutoff) {
        urgencyEl.style.display = 'none';
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        return;
      }

      // Calculate time remaining
      const diff = cutoff - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      // Format - elide hours if less than 1 hour remaining
      if (hours > 0) {
        const m = String(minutes).padStart(2, '0');
        countdownEl.textContent = `${hours} hrs ${m} mins`;
      } else {
        countdownEl.textContent = `${minutes} mins`;
      }
    };

    // Update immediately and then every second
    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, 1000);
  }
}

if (!customElements.get('delivery-estimates')) {
  customElements.define('delivery-estimates', DeliveryEstimates);
}
